
import { Options, Vue, prop } from "vue-class-component";
import SelectSearch from "@/app/ui/components/select-search/index.vue";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import { ManualAssignRequest } from "@/data/payload/api/PickupDashboardRequests";
import { debounce } from "lodash";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ManualAssignData } from "@/domain/entities/PickupDashboard";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";

interface SelectedSellerData {
  id: string;
  sellerName: string;
  sellerPhone: string;
  packageCount: number;
  koli: number;
  weight: number;
}

class Props {
  show = prop<boolean>({ required: true });
  selectedSellers = prop<SelectedSellerData[]>({ required: true });
}

@Options({
  components: {
    SelectSearch
  },
  emits: ["update:show", "assign-success"]
})
export default class ManualAssignModal extends Vue.with(Props) {
  formatPhoneNumber = formatPhoneNumber;

  waNumber(phone: string) {
    return String(phone || "").replace(/\D/g, "");
  }

  selectedCourier = { name: "", value: "" };
  isAssigning = false;
  isSetSchedule = false;
  assignDate = "";
  assignTime = { name: "", value: "" };
  timePickerOpen = false;
  localSelectedSellers: SelectedSellerData[] = [];
  openSuccess = false;
  openError = false;
  errorMessage = "";
  openPartial = false;
  errorAssignData: ManualAssignData[] = [];

  get isSingleSeller(): boolean {
    const sellers = this.localSelectedSellers || [];
    return sellers.length === 1;
  }

  get totalSenders(): number {
    const sellers = this.localSelectedSellers || [];
    return sellers.length;
  }

  get totalPackages(): number {
    const sellers = this.localSelectedSellers || [];
    return sellers.reduce((sum, seller) => sum + seller.packageCount, 0);
  }

  get totalKoli(): number {
    const sellers = this.localSelectedSellers || [];
    return sellers.reduce((sum, seller) => sum + seller.koli, 0);
  }

  get totalWeight(): number {
    const sellers = this.localSelectedSellers || [];
    const total = sellers.reduce((sum, seller) => sum + seller.weight, 0);
    return parseFloat(total.toFixed(2));
  }

  get availableCouriers() {
    return PickupDashboardController.availableCouriers.data;
  }

  get isLoadingCouriers() {
    return PickupDashboardController.isLoadingAvailableCouriers;
  }

  get courierOptions() {
    return this.availableCouriers.map((courier) => ({
      name: `[${courier.courierId}] ${courier.courierName} (${courier.courierType}) ${courier.courierPhone}`,
      value: courier.courierId.toString(),
      courierId: courier.courierId
    }));
  }

  mounted() {
    this.$watch("show", (newVal: boolean) => {
      if (newVal) {
        this.resetModal();
        this.fetchAvailableCouriers("");
        this.localSelectedSellers = [...this.selectedSellers];
      }
    });
  }

  resetModal() {
    this.selectedCourier = { name: "", value: "" };
    this.isAssigning = false;
    this.isSetSchedule = false;
    this.assignDate = "";
    this.assignTime = { name: "", value: "" };
    this.timePickerOpen = false;
    this.localSelectedSellers = [];
    this.openSuccess = false;
    this.openError = false;
    this.openPartial = false;
    this.errorMessage = "";
    this.errorAssignData = [];
  }

  onCloseCourierSelect () {
    this.fetchAvailableCouriers("");
  }

  filterAvailableCouriers = debounce(async (search: string) => {
    this.fetchAvailableCouriers(search);
  }, 250);

  async fetchAvailableCouriers(q: string) {
    await PickupDashboardController.getAvailableCouriers({ q: q });
  }

  removeSeller(index: number) {
    this.localSelectedSellers.splice(index, 1);
  }

  closeModal() {
    this.$emit("update:show", false);
  }

  onSelectCourier(courier: any) {
    this.selectedCourier = courier;
  }

  async handleAssign() {
    try {
      this.isAssigning = true;
      MainAppController.showLoading();
      // Call transfer API through controller
      const payload = new ManualAssignRequest({
        courierId: Number(this.selectedCourier.value) || 0,
        shipmentId: this.localSelectedSellers.map((item) => String(item.id)),
        manualAssignDate: this.isSetSchedule
          ? this.combineWithoutTZ(this.assignDate, this.assignTime.value)
          : ""
      });
      const result = await PickupDashboardController.manualAssign(payload);
      const failedAssign = result.data.filter((item: any) => !item.isSuccess);
      if (failedAssign.length) {
        this.openPartial = true;
        this.errorAssignData = failedAssign;
      } else {
        this.openSuccess = true;
      }
    } catch (error) {
      this.openError = true;
      const response = (error as {
        response?: { data?: { message?: { id?: string } } };
      }).response;
      this.errorMessage = response
        ? response.data?.message?.id || String(this.$t("pickupDashboard.systemError"))
        : "Pastikan koneksi internet Anda aktif dan muat ulang";
    } finally {
      MainAppController.closeLoading();
      this.isAssigning = false;
    }
  }

  onChangeToggle() {
    this.isSetSchedule = !this.isSetSchedule;
  }

  get generateTimeSlots() {
    if (!this.assignDate) return [];
    const result = [];

    const now = new Date(this.assignDate);
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    for (let hour = 0; hour < 24; hour++) {
      const minutes = hour === 23 ? [0] : [0, 30];
      for (const minute of minutes) {
        const totalMinutes = hour * 60 + minute;
        if (totalMinutes < currentMinutes) continue;

        if (totalMinutes < currentMinutes) continue;

        const hh = hour.toString().padStart(2, "0");
        const mm = minute.toString().padStart(2, "0");

        result.push({ name: `${hh}.${mm}`, value: `${hh}.${mm}` });
      }
    }

    return result;
  }

  onSelectTime(item: any) {
    if (this.assignTime.value === item.value) {
      this.assignTime = { name: "", value: "" };
    } else {
      this.assignTime = item;
    }
    this.timePickerOpen = false;
  }

  get disableAssign() {
    const isSetManualAssign = this.isSetSchedule
      ? !this.assignDate || !this.assignTime.value
      : false;
    return !this.selectedCourier.value || isSetManualAssign || this.isAssigning;
  }

  combineWithoutTZ(dateStr: string, timeStr: string): string {
    const d = new Date(dateStr);
    const [hour, minute] = timeStr.split(".").map(Number);

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}T${String(hour).padStart(2, "0")}:${String(
      minute
    ).padStart(2, "0")}:00Z`;
  }

  onCloseModalAssign() {
    this.closeModal();
    this.openError = false;
    this.openSuccess = false;
    this.openPartial = false;
    this.$emit("assign-success");
  }

  mapShipmentType(item: string) {
    const label: any = {
      AGGREGATOR: "Aggregator",
      MARKETPLACE: "Marketplace",
      CUSTOMER_APP: "Customer App",
      CORPORATE: "Corporate"
    };

    return label[item] ?? "-";
  }

  fmtQty(val: number | string) {
    return formatByCountry(Number(val || 0), { type: "quantity" });
  }

  fmtWeight(val: number | string) {
    return formatByCountry(Number(val || 0), { type: "weight", suffix: " kg" });
  }
}
